import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "先核实，再呈现",
    desc: "对来源不清的说法保持谨慎，标注更新说明，让每一条内容都经得起回看。",
  },
  {
    icon: "ri-eye-off-line",
    title: "不打扰，不喧嚣",
    desc: "页面以阅读为主，减少无关弹窗与干扰，让收藏与回看这件事回归简单。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "为留存而设计",
    desc: "收藏、书签、标记等功能围绕「之后还会用到」来设计，而不是一次性的流量。",
  },
  {
    icon: "ri-heart-line",
    title: "尊重当事人",
    desc: "讨论话题时避免过度演绎，聚焦公开信息本身，不过度消费个人隐私。",
  },
];

const voices = [
  {
    name: "林一诺",
    role: "常驻读者",
    text: "最喜欢「只看收藏」这个功能，追的话题集中在一个列表里，不用再一条条翻历史。",
  },
  {
    name: "周允",
    role: "内容爱好者",
    text: "标记功能很好用，我会给待追的话题打上标签，空下来的时候按标签一个个看。",
  },
  {
    name: "陈知夏",
    role: "自由撰稿人",
    text: "热榜的排序比较克制，不会都是重复的话题，作为日常选题参考挺省时间。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 5看料网的内容定位与理念",
    description:
      "了解 5看料网的品牌介绍、网站定位、发展理念与内容原则。我们聚焦网络热点与娱乐动态的聚合，帮助用户通过收藏、书签、标记与关注留住感兴趣的内容。",
    keywords: "5看料网关于我们,内容定位,内容原则,用户口碑,收藏书签标记",
    path: "/about",
  });

  const aboutLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于 5看料网",
    url: "https://tpon.cn/about",
    inLanguage: "zh-CN",
    description: "5看料网的品牌介绍、网站定位、发展理念与内容原则。",
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://tpon.cn/" },
      { "@type": "ListItem", position: 2, name: "关于我们", item: "https://tpon.cn/about" },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative w-full h-[380px] md:h-[460px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20editorial%20abstract%20background%20with%20coral%20red%20amber%20gold%20and%20cream%20paper%20texture%2C%20soft%20geometric%20shapes%2C%20calm%20balanced%20composition%2C%20high%20detail&width=1600&height=700&seq=kanliao-about-01&orientation=landscape"
            alt="关于 5看料网 - 热点话题与娱乐资讯聚合平台"
            title="关于 5看料网"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/60"></div>
          <div className="relative z-10 w-full h-full px-4 md:px-8 flex flex-col justify-center">
            <nav aria-label="面包屑" className="text-xs text-background-100/80">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-accent-300 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li className="text-background-50">关于我们</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-background-50 max-w-3xl leading-tight">
              我们只做一件事：让好内容值得被留下
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-7 text-background-100/90">
              5看料网是一个聚焦网络热点与娱乐动态的内容聚合平台，把「看」与「留下」连在一起。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-500"></span>
                Who We Are
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                品牌介绍
              </h2>
              <div className="mt-5 space-y-4 text-sm md:text-base leading-8 text-foreground-700">
                <p>
                  5看料网诞生于一个很朴素的观察：每天刷到的热点很多，但真正想留下来的却总是散落各处。
                  于是我们把话题聚合起来，并围绕收藏、保存、书签、记录、标记与关注做了一整套工具。
                </p>
                <p>
                  我们不追求把信息堆到最多，而是希望你能轻松找到值得收藏的内容，并在需要的时候顺利找回它。
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-focus-3-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    网站定位
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    轻量、安静的热点聚合站，服务于习惯追话题、也习惯把内容存下来的读者。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-compasses-2-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    发展理念
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    内容要经得起回看，功能要为留存服务，体验要尊重读者的时间与注意力。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-user-heart-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    面向读者
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    喜欢娱乐动态与网络热点、希望有一个属于自己的内容清单的普通用户。
                  </p>
                </div>
                <div className="p-6 rounded-lg bg-background-100 border border-background-200">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-pen-nib-line text-xl"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                    编辑方式
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">
                    小团队人工整理为主，结合热度参考排序，保持节奏稳定、来源清楚。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="w-full bg-background-100 border-y border-background-200">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 uppercase">
                <span className="w-6 h-px bg-primary-500"></span>
                Principles
              </span>
              <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                内容原则
              </h2>
              <p className="mt-4 text-sm md:text-base leading-7 text-foreground-600">
                这四条是我们整理每一条话题时都会参照的标准，也是 5看料网区别于普通资讯列表的地方。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {principles.map((p) => (
                <div
                  key={p.title}
                  className="p-6 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
                >
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${p.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="voices" className="w-full px-4 md:px-8 py-14 md:py-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-medium tracking-widest text-primary-600 uppercase">
              <span className="w-6 h-px bg-primary-500"></span>
              Voices
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              读者怎么说
            </h2>
            <p className="mt-4 text-sm md:text-base leading-7 text-foreground-600">
              来自日常使用收藏与标记功能的读者反馈，是我们持续打磨功能的动力。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {voices.map((v) => (
              <figure
                key={v.name}
                className="flex flex-col h-full p-6 rounded-lg bg-background-100 border border-background-200"
              >
                <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                <blockquote className="mt-4 text-sm leading-7 text-foreground-700 flex-1">
                  {v.text}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-300">
                  <div className="font-heading text-sm font-semibold text-foreground-950">
                    {v.name}
                  </div>
                  <div className="mt-0.5 text-xs text-foreground-500">{v.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full bg-background-100 border-t border-background-200">
          <div className="w-full px-4 md:px-8 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  联系我们
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-600">
                  无论是内容纠错、热点推荐还是功能建议，都欢迎通过下方邮箱或表单与我们联系。
                </p>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-700">
                      <i className="ri-mail-line"></i>
                    </span>
                    <a
                      href="mailto:hello@5kanliao.cn"
                      className="text-sm text-foreground-800 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      hello@5kanliao.cn
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-700">
                      <i className="ri-time-line"></i>
                    </span>
                    <span className="text-sm text-foreground-800">工作日 10:00 - 19:00 回复</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-background-50 border border-background-200 text-primary-700">
                      <i className="ri-question-answer-line"></i>
                    </span>
                    <Link
                      to="/faq"
                      className="text-sm text-foreground-800 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      先看看常见问题
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="lg:col-span-8">
                <FeedbackForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
      <JsonLd id="about-page" data={aboutLd} />
      <JsonLd id="about-breadcrumb" data={breadcrumbLd} />
    </div>
  );
}